export const COOKIE_CONSENT_STORAGE_KEY = 'caloriescience_cookie_consent';
export const COOKIE_PREFERENCES_OPEN_EVENT = 'caloriescience:open-cookie-preferences';
export const COOKIE_CONSENT_UPDATED_EVENT = 'caloriescience:cookie-consent-updated';

export interface CookieConsentPreferences {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  version: number;
  updatedAt: string;
}

export type CookieConsentChoice = Pick<CookieConsentPreferences, 'analytics' | 'marketing'>;

export const COOKIE_CONSENT_VERSION = 2;
const CONSENT_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;
const SHARED_CONSENT_HOSTS = new Set(['caloriescience.ai', 'www.caloriescience.ai', 'app.caloriescience.ai']);

const isBrowser = () => typeof window !== 'undefined';

/** Only the production website and app may share consent/attribution cookies. */
export const getSharedCookieDomain = (): string | undefined =>
  isBrowser() && SHARED_CONSENT_HOSTS.has(window.location.hostname.toLowerCase())
    ? '.caloriescience.ai'
    : undefined;

const toConsentRecord = (choice: CookieConsentChoice): CookieConsentPreferences => ({
  necessary: true,
  analytics: choice.analytics,
  marketing: choice.marketing,
  version: COOKIE_CONSENT_VERSION,
  updatedAt: new Date().toISOString(),
});

const isValidConsentRecord = (value: unknown): value is CookieConsentPreferences => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Partial<CookieConsentPreferences>;
  return (
    record.necessary === true &&
    typeof record.analytics === 'boolean' &&
    typeof record.marketing === 'boolean' &&
    record.version === COOKIE_CONSENT_VERSION &&
    typeof record.updatedAt === 'string' &&
    Number.isFinite(Date.parse(record.updatedAt))
  );
};

const parseConsent = (raw: string | null): CookieConsentPreferences | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return isValidConsentRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const readCookieConsent = (): CookieConsentPreferences | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const cookie = document.cookie.split(';').map((entry) => entry.trim())
      .find((entry) => entry.startsWith(`${COOKIE_CONSENT_STORAGE_KEY}=`));
    return cookie ? parseConsent(decodeURIComponent(cookie.slice(COOKIE_CONSENT_STORAGE_KEY.length + 1))) : null;
  } catch {
    return null;
  }
};

export const saveCookieConsent = (choice: CookieConsentChoice): CookieConsentPreferences => {
  const record = toConsentRecord(choice);

  if (isBrowser()) {
    const serialized = JSON.stringify(record);
    try {
      const domain = getSharedCookieDomain();
      const secure = domain || window.location.protocol === 'https:' ? '; Secure' : '';
      // Remove older exact-host copies so the shared preference is unambiguous.
      if (domain) document.cookie = `${COOKIE_CONSENT_STORAGE_KEY}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
      document.cookie = `${COOKIE_CONSENT_STORAGE_KEY}=${encodeURIComponent(serialized)}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}${domain ? `; Domain=${domain}` : ''}`;
    } catch { /* If cookies are blocked, readCookieConsent continues to deny tracking. */ }
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_UPDATED_EVENT, { detail: record }));
  }

  return record;
};

export const openCookiePreferences = () => {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(new Event(COOKIE_PREFERENCES_OPEN_EVENT));
};
